import React from 'react';
import {array, bool, func, object, string} from 'prop-types';

import { useFormik } from 'formik';
import * as Yup from 'yup';

import { get } from 'lodash';

import {connect} from 'react-redux';

import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import TextField from '@material-ui/core/TextField';

import EsmsDialog from '../../components/EsmsDialog';

import * as WizardActions from '../../actions/wizardActions';

import classes from './EditEntityModal.module.css';

const ValidationSchema = Yup.object().shape({
    name:          Yup.string().max(45).required(),
    equipmentType: Yup.string().required(),
    manufacturer:  Yup.string(),
    modelNumber:   Yup.string().max(45),
    serialNumber:  Yup.string().max(45)
});

const DEFAULT_FORM_VALUES = {
    name:          '',
    equipmentType: '',
    manufacturer:  '',
    modelNumber:   '',
    serialNumber:  ''
};

const InitialValues = {...DEFAULT_FORM_VALUES};

const CustomizeEquipmentModal = ({
    close,
    device,
    equipmentTypes,
    manufacturers,
    open,
    title,
    toggleLoading,
    ...props
}) => {

    const formik = useFormik({
        initialValues:      InitialValues,
        validationSchema:   ValidationSchema,
        enableReinitialize: true,
        onSubmit:           async (values, { setSubmitting }) => {
            const {
                updateTargetDevices
            } = props;

            setSubmitting(true);

            device.name = values.name;
            device.equipmentType = values.equipmentType;
            device.manufacturer = values.manufacturer;
            device.modelNumber = values.modelNumber;
            device.serialNumber = values.serialNumber;

            updateTargetDevices(device);

            setSubmitting(false);

            handleClose();
        }
    });

    const handleReset = () => {
        if (InitialValues.name.length) {
            Object.assign(InitialValues, DEFAULT_FORM_VALUES);
        }

        InitialValues.name = get(device, 'name', '');
        InitialValues.equipmentType = get(device, 'equipmentType', '');
        InitialValues.manufacturer = get(device, 'manufacturer', '');
        InitialValues.modelNumber = get(device, 'modelNumber', '');
        InitialValues.serialNumber = get(device, 'serialNumber', '');

        formik.handleReset(InitialValues);
    };

    const handleClose = () => {
        close();
    };

    return (
        <EsmsDialog
            open={open}
            onClose={handleClose}
            title={title}
            onRendered={handleReset}
            onConfirm={formik.handleSubmit}
            disabled={formik.isSubmitting}
            confirmText="Save"
        >
            <div className={classes.formColumn}>
                <div className={classes.labelColumn}>
                    <div className={classes.labelHeader}>
                        <img src="/assets/images/equipmentTreeIcon.svg" alt="Equipment Icon" />
                        <span>Customize Equipment</span>
                    </div>
                    <div className={classes.labelContent}>
                        <p>
                            Enter a unique name.
                        </p>
                        <p>
                            Select the equipment type.
                        </p>
                        <p>
                            Select a manufacturer. (Optional)
                        </p>
                        <p>
                            Enter a model number and a serial number. (Optional)
                        </p>
                    </div>
                </div>
                <div className={classes.inputColumn}>
                    <div className={classes.inputs}>
                        <FormControl className={classes.inputWrapper}>
                            <TextField
                                id="name"
                                name="name"
                                type="text"
                                value={formik.values.name}
                                label="Equipment Name"
                                onChange={formik.handleChange}
                                InputLabelProps={{
                                    classes: {
                                        error:   classes.error,
                                        focused: classes.labelFocused,
                                        root:    classes.label
                                    }
                                }}
                                InputProps={{
                                    onBlur:  formik.handleBlur,
                                    classes: {
                                        error:     classes.cssErrorUnderline,
                                        underline: classes.cssUnderline,
                                        root:      classes.input
                                    }
                                }}
                                FormHelperTextProps={{
                                    classes: {
                                        error: classes.error
                                    }
                                }}
                                error={Boolean(formik.errors.name) && Boolean(formik.touched.name)}
                                helperText={Boolean(formik.touched.name) && formik.errors.name}
                            />
                        </FormControl>
                        <FormControl className={classes.inputWrapper}>
                            <InputLabel
                                htmlFor="equipmentType"
                                classes={{
                                    error:   classes.error,
                                    focused: classes.labelFocused,
                                    root:    classes.label
                                }}
                                error={Boolean(formik.errors.equipmentType) && Boolean(formik.touched.equipmentType)}
                            >
                                Equipment Type
                            </InputLabel>
                            <Select
                                name="equipmentType"
                                id="equipmentType"
                                value={formik.values.equipmentType}
                                onChange={formik.handleChange}
                                classes={{
                                    root: classes.select
                                }}
                                inputProps={{
                                    onBlur: formik.handleBlur
                                }}
                                error={Boolean(formik.errors.equipmentType) && Boolean(formik.touched.equipmentType)}
                            >
                                {
                                    equipmentTypes.map(type =>
                                        <MenuItem
                                            key={type.id}
                                            value={type.id}
                                        >
                                            {type.name}
                                        </MenuItem>
                                    )
                                }
                            </Select>
                            {formik.touched.equipmentType &&
                             formik.errors.equipmentType && <FormHelperText className={classes.error}>{formik.errors.equipmentType}</FormHelperText>}
                        </FormControl>
                        <FormControl className={classes.inputWrapper}>
                            <InputLabel
                                htmlFor="manufacturer"
                                classes={{
                                    error:   classes.error,
                                    focused: classes.labelFocused,
                                    root:    classes.label
                                }}
                                error={Boolean(formik.errors.manufacturer) && Boolean(formik.touched.manufacturer)}
                            >
                                Manufacturer
                            </InputLabel>
                            <Select
                                name="manufacturer"
                                id="manufacturer"
                                value={formik.values.manufacturer}
                                onChange={formik.handleChange}
                                classes={{
                                    root: classes.select
                                }}
                                inputProps={{
                                    onBlur: formik.handleBlur
                                }}
                                error={Boolean(formik.errors.manufacturer) && Boolean(formik.touched.manufacturer)}
                            >
                                {
                                    manufacturers.map(manufacturer =>
                                        <MenuItem
                                            key={manufacturer.id}
                                            value={manufacturer.id}
                                        >
                                            {manufacturer.name}
                                        </MenuItem>
                                    )
                                }
                            </Select>
                            {formik.touched.manufacturer &&
                             formik.errors.manufacturer && <FormHelperText className={classes.error}>{formik.errors.manufacturer}</FormHelperText>}
                        </FormControl>
                        <FormControl className={classes.inputWrapper}>
                            <TextField
                                id="modelNumber"
                                name="modelNumber"
                                type="text"
                                value={formik.values.modelNumber}
                                label="Model Number"
                                onChange={formik.handleChange}
                                InputLabelProps={{
                                    classes: {
                                        error:   classes.error,
                                        focused: classes.labelFocused,
                                        root:    classes.label
                                    }
                                }}
                                InputProps={{
                                    onBlur:  formik.handleBlur,
                                    classes: {
                                        error:     classes.cssErrorUnderline,
                                        underline: classes.cssUnderline,
                                        root:      classes.input
                                    }
                                }}
                                FormHelperTextProps={{
                                    classes: {
                                        error: classes.error
                                    }
                                }}
                                error={Boolean(formik.errors.modelNumber) && Boolean(formik.touched.modelNumber)}
                                helperText={Boolean(formik.touched.modelNumber) && formik.errors.modelNumber}
                            />
                        </FormControl>
                        <FormControl className={classes.inputWrapper}>
                            <TextField
                                id="serialNumber"
                                name="serialNumber"
                                type="text"
                                value={formik.values.serialNumber}
                                label="Serial Number"
                                onChange={formik.handleChange}
                                InputLabelProps={{
                                    classes: {
                                        error:   classes.error,
                                        focused: classes.labelFocused,
                                        root:    classes.label
                                    }
                                }}
                                InputProps={{
                                    onBlur:  formik.handleBlur,
                                    classes: {
                                        error:     classes.cssErrorUnderline,
                                        underline: classes.cssUnderline,
                                        root:      classes.input
                                    }
                                }}
                                FormHelperTextProps={{
                                    classes: {
                                        error: classes.error
                                    }
                                }}
                                error={Boolean(formik.errors.serialNumber) && Boolean(formik.touched.serialNumber)}
                                helperText={Boolean(formik.touched.serialNumber) && formik.errors.serialNumber}
                            />
                        </FormControl>
                    </div>
                </div>
            </div>
        </EsmsDialog>
    );
};

CustomizeEquipmentModal.defaultProps = {
    close:          () => {},
    device:         {},
    equipmentTypes: [],
    manufacturers:  [],
    open:           false,
    title:          ''
};

CustomizeEquipmentModal.propTypes = {
    close:          func,
    device:         object,
    equipmentTypes: array,
    manufacturers:  array,
    open:           bool,
    title:          string
};

const mapStateToProps = state => {
    return {
        equipmentTypes: state.equipmentTypes.data,
        manufacturers:  state.architect.manufacturers
    };
};

const mapDispatchToProps = {
    ...WizardActions
};

export default connect(mapStateToProps, mapDispatchToProps)(CustomizeEquipmentModal);
